import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom"; // ⬅ add useNavigate
import {
  LayoutDashboard,
  Upload,
  LogOut,
  Menu,
  X,
  ChevronRight,
} from "lucide-react";

function NavItem({ to, icon: Icon, label, badge, onClick, isActive }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={`relative flex items-center gap-3 px-3 py-2 rounded-xl transition-colors group
      ${
        isActive ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50"
      }`}
    >
      <span
        className={`absolute left-0 top-1/2 -translate-y-1/2 h-7 w-[3px] rounded-r-full 
        bg-gradient-to-b from-blue-500 to-blue-500 transition-opacity
        ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-40"}`}
      />
      <Icon className="h-5 w-5" />
      <span className="flex-1 truncate">{label}</span>
      {badge && (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
          {badge}
        </span>
      )}
      <ChevronRight
        className={`h-4 w-4 transition-transform ${
          isActive ? "translate-x-0" : "-translate-x-0.5 opacity-40"
        }`}
      />
    </NavLink>
  );
}

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate(); // ⬅ react-router navigation

  const navItems = [
    { label: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
    {
      label: "Upload Token",
      path: "/admin/upload-token",
      icon: Upload,
      badge: "NEW",
    },
  ];

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  // 🚀 Handle logout
  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("token_type");
    setOpen(false);
    navigate("/admin/sign-in", { replace: true });
  };

  return (
    <div>
      {/* Toggle Button */}
      <button
        onClick={() => setOpen((s) => !s)}
        className="fixed top-4 left-4 z-50 p-2 rounded-xl bg-white/90 backdrop-blur shadow hover:shadow-md border"
        aria-label={open ? "Close sidebar" : "Open sidebar"}
      >
        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar Panel */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-72 max-w-[85vw] border-r
         bg-white/80 backdrop-blur-xl shadow-2xl transition-transform duration-300 ${
           open ? "translate-x-0" : "-translate-x-full"
         }`}
      >
        {/* Brand/Header */}
        <div className="px-4 py-4 border-b flex items-center justify-between bg-gradient-to-r from-white/80 to-white/60">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-600 text-white grid place-items-center shadow">
              🏢
            </div>
            <div className="leading-tight">
              <div className="font-semibold">Admin</div>
              <div className="text-[11px] text-gray-500">Control Center</div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-2 rounded-xl hover:bg-gray-100"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <div className="p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-400 px-2 mb-2">
            Navigation
          </div>
          <nav className="space-y-1">
            {navItems.map(({ label, path, icon, badge }) => (
              <NavItem
                key={path}
                to={path}
                icon={icon}
                label={label}
                isActive={isActive(path)}
                badge={badge}
                onClick={() => setOpen(false)}
              />
            ))}
          </nav>
        </div>

        {/* Logout */}
        <div className="mt-auto p-3">
          <button
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-red-600 hover:bg-red-50"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5" /> Logout
          </button>
        </div>
      </aside>
    </div>
  );
}
